import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Notification } from '../types';
import { testDataApi, interpretationsApi } from '../services/api';
import { useVisiblePolling } from '../hooks';

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

const POLL_INTERVAL = 60000; // 1 minuto

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Genera notificaciones localmente basadas en datos del sistema
  const generateLocalNotifications = useCallback(async (): Promise<Notification[]> => {
    const localNotifications: Notification[] = [];
    const now = new Date();

    try {
      // Obtener datos pendientes para generar alertas de SLA
      const testData = await testDataApi.getPending();

      for (const patient of testData.patients) {
        if (patient.sla_exceeded) {
          // SLA excedido - prioridad alta
          localNotifications.push({
            notification_id: `sla-exceeded-${patient.patient_key}`,
            type: 'sla_exceeded',
            priority: 'high',
            title: 'SLA Excedido',
            message: `${patient.patient_name} ha excedido el tiempo de SLA`,
            created_at: now.toISOString(),
            read: false,
            patient_key: patient.patient_key,
            patient_name: patient.patient_name,
          });
        } else if (patient.hours_remaining <= 12) {
          // SLA próximo a vencer - prioridad media
          localNotifications.push({
            notification_id: `sla-warning-${patient.patient_key}`,
            type: 'sla_warning',
            priority: 'medium',
            title: 'SLA Próximo a Vencer',
            message: `${patient.patient_name} - ${Math.round(patient.hours_remaining)}h restantes`,
            created_at: now.toISOString(),
            read: false,
            patient_key: patient.patient_key,
            patient_name: patient.patient_name,
          });
        }
      }

      // Obtener interpretaciones pendientes de revisión
      const pendingInterps = await interpretationsApi.getPending({ limit: 10 });

      // Notificar sobre interpretaciones que llevan más de 1 hora pendientes
      for (const interp of pendingInterps.items) {
        if (interp.status === 'pending_review') {
          const createdAt = new Date(interp.created_at);
          const hoursPending = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

          if (hoursPending >= 1) {
            const patientName = interp.raw_data?.patient_info?.name || interp.patient_id;
            localNotifications.push({
              notification_id: `interp-pending-${interp.interpretation_id}`,
              type: 'interpretation_ready',
              priority: hoursPending >= 4 ? 'high' : 'medium',
              title: 'Interpretación Pendiente',
              message: `${patientName} espera revisión desde hace ${Math.round(hoursPending)}h`,
              created_at: interp.created_at,
              read: false,
              patient_key: interp.patient_id,
              patient_name: patientName,
              interpretation_id: interp.interpretation_id,
            });
          }
        }
      }
    } catch (err) {
      console.error('Error generating local notifications:', err);
    }

    // Ordenar por prioridad y fecha
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    localNotifications.sort((a, b) => {
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return localNotifications;
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Generar notificaciones localmente basadas en datos del sistema
      // (No hay endpoint de backend para notificaciones)
      const localNotifs = await generateLocalNotifications();
      setNotifications(localNotifs);
      setUnreadCount(localNotifs.filter(n => !n.read).length);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  }, [generateLocalNotifications]);

  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.notification_id === notificationId
          ? { ...n, read: true, read_at: new Date().toISOString() }
          : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    const now = new Date().toISOString();
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true, read_at: now }))
    );
    setUnreadCount(0);
  }, []);

  const deleteNotification = useCallback((notificationId: string) => {
    setNotifications(prev => {
      const notification = prev.find(n => n.notification_id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(count => Math.max(0, count - 1));
      }
      return prev.filter(n => n.notification_id !== notificationId);
    });
  }, []);

  // Carga inicial: ocurre siempre, también con la pestaña oculta. Sin ella no habría
  // nada que pintar cuando el auditor mire.
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Polling para actualizaciones, en silencio total mientras la pestaña esté oculta:
  // las notificaciones se derivan localmente de dos endpoints que se consultan igual
  // al volver, así que un poll de fondo no descubriría nada que se pierda después.
  useVisiblePolling(fetchNotifications, POLL_INTERVAL);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
